<script setup>
import { getCurrentInstance, ref } from "vue";
import AppLink from "@Components/AppLink.vue";
import Modal from "./Modal.vue";
import { ChevronRightIcon } from "@heroicons/vue/24/outline";
import { useI18n } from "vue-i18n";

defineProps({
    title: {
        type: String,
    },
    tools: {
        type: Array,
        default: [],
    },
    customTools: {
        type: Array,
        default: [],
    },
});

const { t: __ } = useI18n();

const modal = ref();

const show = () => {
    modal.value?.show();
};

const hide = () => {
    modal.value?.hide();
};

const isToolDisabled = (tool) => {
    if (!tool.href && !tool.onClick) {
        return true;
    }

    return false;
};

const getToolComponent = (tool) => {
    if (tool.href) {
        return AppLink;
    }

    return "div";
};

const getToolAttributes = (tool) => {
    let attrs = {};

    if (tool.href) {
        attrs.to = tool.href;
        attrs.target = tool.target;
    }

    return attrs;
};

const getToolEvents = (tool) => {
    let events = {};

    if (tool.href) {
        events.click = () => {
            modal.value.hide();
        };
    } else if (tool.onClick) {
        events.click = () => {
            tool.onClick();
            modal.value.hide();
        };
    }

    return events;
};

defineExpose({ show, hide });
</script>

<template>
    <Modal ref="modal">
        <div class="relative">
            <img :src="getCurrentInstance().appContext.config.globalProperties.$asset('img/autopilot.jpg')" class="w-full h-36 object-cover" />
            <div class="to-black w-full h-full absolute inset-0 bg-gradient-to-b from-transparent"></div>
            <div class="absolute inset-0 flex items-end px-4 py-4 lg:px-8 sm:px-6">
                <div class="text-3xl font-semibold text-gray-900 dark:text-white">{{ __("Génération Autopilot IA") }}</div>
            </div>
        </div>
        <div class="px-4 py-6 lg:px-8 sm:px-6">
            <div v-if="title" class="mb-6 text-lg font-semibold text-gray-900 dark:text-white">{{ title }}</div>
            <div class="space-y-1">
                <component
                    v-for="tool in tools"
                    :is="getToolComponent(tool)"
                    v-bind="getToolAttributes(tool)"
                    v-on="getToolEvents(tool)"
                    :class="[
                        !isToolDisabled(tool) ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 group' : '',
                        'relative flex items-center px-3 py-2',
                    ]">
                    <div
                        :class="[
                            'group-hover:bg-gray-950 dark:group-hover:bg-gray-300',
                            'pointer-events-none absolute top-0 left-0 bottom-0 w-0.5 bg-transparent',
                        ]"></div>
                    <div class="flex flex-col flex-1">
                        <div class="flex items-center space-x-3">
                            <component :is="tool.icon" :class="['text-gray-950 dark:text-white', 'w-5 h-5']" />
                            <div class="text-base font-semibold text-gray-900 dark:text-white">{{ tool.name }}</div>
                        </div>
                    </div>

                    <div class="flex-none ml-3">
                        <ChevronRightIcon :class="['group-hover:text-gray-950 dark:group-hover:text-white', 'w-5 h-5 text-gray-700 dark:text-gray-300']" />
                    </div>
                </component>
            </div>
            <template v-if="customTools.length > 0">
                <div class="flex items-center my-3">
                    <div class="h-px flex-1 bg-gray-300 dark:bg-gray-700"></div>
                    <div class="mx-4 text-gray-500 dark:text-gray-400">{{ __("Ou") }}</div>
                    <div class="h-px flex-1 bg-gray-300 dark:bg-gray-700"></div>
                </div>
                <div class="space-y-1">
                    <component
                        v-for="tool in customTools"
                        :is="getToolComponent(tool)"
                        v-bind="getToolAttributes(tool)"
                        v-on="getToolEvents(tool)"
                        :class="[
                            !isToolDisabled(tool) ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 group' : '',
                            'relative flex items-center px-3 py-2',
                        ]">
                        <div
                            :class="[
                                'group-hover:bg-gray-950 dark:group-hover:bg-gray-300',
                                'pointer-events-none absolute top-0 left-0 bottom-0 w-0.5 bg-transparent',
                            ]"></div>
                        <div class="flex flex-col flex-1">
                            <div class="flex items-center space-x-3">
                                <component :is="tool.icon" :class="['text-gray-950 dark:text-white', 'w-5 h-5']" />
                                <div class="text-base font-semibold text-gray-900 dark:text-white">{{ tool.name }}</div>
                            </div>
                        </div>

                        <div class="flex-none ml-3">
                            <ChevronRightIcon :class="['group-hover:text-gray-950 dark:group-hover:text-white', 'w-5 h-5 text-gray-700 dark:text-gray-300']" />
                        </div>
                    </component>
                </div>
            </template>
        </div>
    </Modal>
</template>
